import { Modal } from 'components/Modal';
import { Title } from 'components/Title';
import modalStyles from './BeneficiariesUpdateModal.module.scss';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { Button } from 'ui/Button';
import { useUpdateBeneficiarLimitMutation } from 'redux/Api/beneficiariesApi/beneficiariesApi';

interface ModalUpdateLimitProps {
  id: string;
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
}

export default function ModalUpdateLimit({
  id,
  isOpen,
  onClose,
  refetch,
}: ModalUpdateLimitProps) {
  const { infoBeneficiar } = useSelector((state: RootState) => state.beneficiarSlice);

  const [pendingLimitData, setPendingLimitData] = useState<{
    min_limit: number;
    max_limit: number;
  } | null>(null);

  const [confirmSaveLimitOpen, setConfirmSaveLimitOpen] = useState(false);
  const [updateBeneficiarLimit, { isLoading: isUpdatingLimit }] =
    useUpdateBeneficiarLimitMutation();
  const [showError, setShowError] = useState(false);
  const [errorText, setErrorText] = useState<string>('');

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      min_limit: infoBeneficiar?.order_limits?.min || 0,
      max_limit: infoBeneficiar?.order_limits?.max || 0,
    },
  });

  useEffect(() => {
    if (infoBeneficiar && isOpen) {
      setValue('min_limit', infoBeneficiar?.order_limits?.min || 0);
      setValue('max_limit', infoBeneficiar?.order_limits?.max || 0);
    }
  }, [infoBeneficiar, isOpen, setValue]);

  const onSubmitLimit = (formData: { min_limit: number; max_limit: number }) => {
    setPendingLimitData(formData);
    setConfirmSaveLimitOpen(true);
  };

  const confirmSaveLimit = async () => {
    if (!id || !pendingLimitData) return;

    const expectedPassword = process.env.REACT_APP_PASSWORD_UPDATE_PROFILE || '';

    const enteredPassword = window.prompt('Введите пароль для подтверждения изменений');

    if (enteredPassword === null) {
      return;
    }

    if (enteredPassword !== expectedPassword) {
      window.alert('Неверный пароль');
      return;
    }

    try {
      const body = {
        min_limit: Number(pendingLimitData.min_limit) || 0,
        max_limit: Number(pendingLimitData.max_limit) || 0,
      };

      await updateBeneficiarLimit({ id, body }).unwrap();
      await refetch();
      onClose();
      setConfirmSaveLimitOpen(false);
      setPendingLimitData(null);
    } catch (error: unknown) {
      const apiError = error as { data?: { message?: string } };
      setConfirmSaveLimitOpen(false);
      setErrorText(apiError?.data?.message || 'Ошибка при обновлении данных заказчика');
      setShowError(true);
    }
  };

  const handleClose = () => {
    setPendingLimitData(null);
    setConfirmSaveLimitOpen(false);
    onClose();
    setErrorText('');
    setShowError(false);
  };
  return (
    <>
      {isOpen && (
        <Modal setOpen={handleClose}>
          <div className={modalStyles.root}>
            <Title className={modalStyles.title} title='Изменение лимита суммы заказа' />
            <form autoComplete='off' onSubmit={handleSubmit(onSubmitLimit)}>
              <div className={modalStyles.rootInput}>
                <input
                  className={modalStyles.input}
                  type='number'
                  step='0.01'
                  {...register('min_limit', {
                    required: 'Поле обязательно',
                    min: {
                      value: 0,
                      message: 'Минимальная сумма (₽)',
                    },
                  })}
                />
                <span className={modalStyles.rootInput_placeholder}>
                  Минимальная сумма (₽)
                </span>
                {errors.min_limit && (
                  <p className={modalStyles.errors}>
                    {errors.min_limit?.message as string}
                  </p>
                )}
              </div>

              <div className={modalStyles.rootInput}>
                <input
                  className={modalStyles.input}
                  type='number'
                  step='0.01'
                  {...register('max_limit', {
                    required: 'Поле обязательно',
                    min: {
                      value: 0,
                      message: 'Минимум 0',
                    },
                  })}
                />
                <span className={modalStyles.rootInput_placeholder}>
                  Максимальная сумма (₽)
                </span>
                {errors.max_limit && (
                  <p className={modalStyles.errors}>
                    {errors.min_limit?.message as string}
                  </p>
                )}
              </div>

              {showError && <p style={{ color: 'red' }}>{errorText}</p>}

              <div className={modalStyles.buttons}>
                <Button
                  onClick={handleClose}
                  className={modalStyles.button_cancel}
                  theme='outline'>
                  Отмена
                </Button>
                <Button
                  disabled={isUpdatingLimit}
                  loading={isUpdatingLimit}
                  onClick={handleSubmit(onSubmitLimit)}
                  className={modalStyles.button}
                  theme='filed'>
                  Сохранить
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}
      {confirmSaveLimitOpen && (
        <Modal setOpen={setConfirmSaveLimitOpen}>
          <div className={modalStyles.root}>
            <Title className={modalStyles.title} title='Подтверждение сохранения' />
            <p style={{ marginBottom: '20px', fontSize: '14px' }}>
              Вы уверены, что хотите сохранить изменения лимита?
            </p>
            <div className={modalStyles.buttons}>
              <Button
                onClick={() => setConfirmSaveLimitOpen(false)}
                className={modalStyles.button_cancel}
                theme='outline'>
                Отмена
              </Button>
              <Button
                disabled={isUpdatingLimit}
                loading={isUpdatingLimit}
                onClick={confirmSaveLimit}
                className={modalStyles.button}
                theme='filed'>
                Подтвердить
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
